/**
 * Import masivo de fichas desde .md / .html / .json preparados por IA.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, FileUp, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { notifyErr, notifyOk } from '@/lib/notify';
import { PageNoticePanel } from '../PageNoticePanel';
import { rememberNotice, type PageNotice } from '../pageNotice';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { fichasImportQueuePath } from '@/constants/store-nav';
import { FICHA_HELP_PATH } from './lib/productFichaTemplate';
import { useApplyCatalogProductContentBatch } from '../catalog/hooks/useStoreCatalog';
import type { CatalogBatchResult } from '../catalog/types';
import {
    parseProductFichaJsonBatch,
    parseProductFichaText,
    parsedToBatchItem,
    type ParsedFichaFile,
} from './lib/parseProductFichaFile';
import { FICHA_AI_SYSTEM_PROMPT, FICHA_MD_TEMPLATE } from './lib/productFichaTemplate';
function parseFiles(files: Array<{ name: string; text: string }>): ParsedFichaFile[] {
    const out: ParsedFichaFile[] = [];
    for (const file of files) {
        if (file.name.toLowerCase().endsWith('.json')) {
            try {
                out.push(...parseProductFichaJsonBatch(file.name, file.text));
            } catch (e) {
                out.push({
                    filename: file.name,
                    seoSlug: '',
                    patch: {},
                    warnings: [],
                    leak: false,
                    error: e instanceof Error ? e.message : String(e),
                });
            }
        } else {
            out.push(parseProductFichaText(file.name, file.text));
        }
    }
    return out;
}

export function CatalogFichasImportPage() {
    const navigate = useNavigate();
    const mut = useApplyCatalogProductContentBatch();
    const [parsed, setParsed] = useState<ParsedFichaFile[]>([]);
    const [paste, setPaste] = useState('');
    const [result, setResult] = useState<CatalogBatchResult | null>(null);
    const [notices, setNotices] = useState<PageNotice[]>([]);
    const [copied, setCopied] = useState<'prompt' | 'tpl' | null>(null);

    const record = (notice: Omit<PageNotice, 'id'>) => {
        setNotices((prev) => rememberNotice(prev, notice));
    };

    const ready = useMemo(
        () => parsed.filter((p) => p.seoSlug && !p.error && !p.leak),
        [parsed],
    );
    const blocked = parsed.length - ready.length;

    const onFiles = async (list: FileList | null) => {
        if (!list?.length) return;
        setResult(null);
        const files: Array<{ name: string; text: string }> = [];
        for (const file of Array.from(list)) {
            files.push({ name: file.name, text: await file.text() });
        }
        setParsed(parseFiles(files));
    };

    const onPasteParse = () => {
        if (!paste.trim()) return;
        setResult(null);
        const looksJson = paste.trim().startsWith('{') || paste.trim().startsWith('[');
        setParsed(parseFiles([{
            name: looksJson ? 'pegado.json' : 'pegado.md',
            text: paste,
        }]));
    };

    const downloadTemplate = () => {
        const blob = new Blob([FICHA_MD_TEMPLATE], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'plantilla-ficha-producto.md';
        a.click();
        URL.revokeObjectURL(url);
    };

    const copy = async (which: 'prompt' | 'tpl') => {
        await navigator.clipboard.writeText(which === 'prompt' ? FICHA_AI_SYSTEM_PROMPT : FICHA_MD_TEMPLATE);
        setCopied(which);
        window.setTimeout(() => setCopied(null), 2000);
    };

    const run = async (dryRun: boolean) => {
        if (ready.length === 0) {
            const text = 'No hay fichas válidas para enviar';
            record({ type: 'err', text });
            notifyErr(text);
            return;
        }
        try {
            const data = await mut.mutateAsync({
                dryRun,
                items: ready.map(parsedToBatchItem),
            });
            setResult(data);
            const text = dryRun
                ? `Simulación hecha. ${data.applied} de ${data.total}. No se ha guardado nada.`
                : `Lote enviado a revisión. ${data.applied} de ${data.total}.`;
            record({ type: 'ok', text });
            notifyOk(text);
        } catch (e: unknown) {
            const text = e instanceof Error ? e.message : String(e);
            record({ type: 'err', text });
            notifyErr(text);
        }
    };

    return (
        <div className="space-y-5" data-page="catalog-fichas-import">
            <PageHeader
                title="Importar fichas"
                description="Sube .md, .html o un JSON de lote. Siempre quedan En revisión; nadie publica a ciegas."
            >
                <Button type="button" variant="outline" size="sm" onClick={() => navigate(fichasImportQueuePath())}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Cola
                </Button>
            </PageHeader>

            <PageNoticePanel notices={notices} />

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="rounded-xl border bg-card p-4 space-y-3">
                    <h2 className="font-semibold text-sm">1. Prepáralo con la IA</h2>
                    <p className="text-sm text-muted-foreground">
                        Copia el prompt, pégalo en Claude o ChatGPT, y pídele un .md por producto o un JSON con varias fichas.
                        Las reglas están en{' '}
                        <Link className="text-primary underline" to={FICHA_HELP_PATH}>Instrucciones</Link>.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => copy('prompt')}>
                            <Copy className="h-4 w-4 mr-1" />
                            {copied === 'prompt' ? 'Prompt copiado' : 'Copiar prompt'}
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => copy('tpl')}>
                            {copied === 'tpl' ? 'Plantilla copiada' : 'Copiar plantilla'}
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
                            Descargar plantilla.md
                        </Button>
                    </div>
                    <pre className="text-[11px] bg-muted/50 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap">
                        {FICHA_AI_SYSTEM_PROMPT}
                    </pre>
                </div>
                <div className="rounded-xl border bg-card p-4 space-y-3">
                    <h2 className="font-semibold text-sm">2. Súbelo aquí</h2>
                    <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 cursor-pointer hover:bg-muted/30">
                        <FileUp className="h-6 w-6 text-muted-foreground" />
                        <span className="text-sm font-medium">Suelta o elige varios .md / .html / .json</span>
                        <input
                            type="file"
                            className="hidden"
                            accept=".md,.html,.json,.txt,text/markdown,text/html,application/json"
                            multiple
                            onChange={(e) => onFiles(e.target.files)}
                        />
                    </label>
                    <Textarea
                        rows={5}
                        placeholder="O pega aquí un .md o un JSON { products: [...] }"
                        value={paste}
                        onChange={(e) => setPaste(e.target.value)}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={onPasteParse}>
                        Parsear texto pegado
                    </Button>
                </div>
            </div>

            {parsed.length > 0 && (
                <div className="rounded-xl border overflow-hidden">
                    <div className="px-3 py-2 bg-muted/40 text-sm flex justify-between">
                        <span>{parsed.length} archivos · {ready.length} listos · {blocked} con problema</span>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Archivo</th>
                                <th className="px-3 py-2">Slug</th>
                                <th className="px-3 py-2">Campos</th>
                                <th className="px-3 py-2">Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {parsed.map((p) => (
                                <tr key={p.filename} className="border-t">
                                    <td className="px-3 py-2">{p.filename}</td>
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {p.nuevoSeoSlug ? `${p.seoSlug} → ${p.nuevoSeoSlug}` : (p.seoSlug || '—')}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {[...Object.keys(p.patch), ...(p.nuevoSeoSlug ? ['seo_slug'] : [])].join(', ') || '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                        {p.error || p.leak ? (
                                            <span className="text-destructive text-xs">{p.error || p.warnings.join(' ')}</span>
                                        ) : (
                                            <span className="text-emerald-700 dark:text-emerald-400 text-xs">Listo</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="p-3 flex flex-wrap gap-2 border-t">
                        <Button type="button" variant="outline" disabled={mut.isPending || ready.length === 0} onClick={() => run(true)}>
                            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                            Dry-run (no guarda)
                        </Button>
                        <Button type="button" disabled={mut.isPending || ready.length === 0} onClick={() => run(false)}>
                            Aplicar a revisión
                        </Button>
                    </div>
                </div>
            )}

            {result && (
                <div className="rounded-xl border overflow-hidden">
                    <div className="px-3 py-2 bg-muted/40 text-sm">
                        {result.dryRun ? 'Simulación' : 'Aplicado'}: {result.applied} de {result.total}
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2">Slug</th>
                                <th className="px-3 py-2">Producto</th>
                                <th className="px-3 py-2">Resultado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(result.items || []).map((row) => (
                                <tr key={`${row.index}-${row.seoSlug}`} className="border-t">
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {row.productId ? (
                                            <Link className="underline" to={`/store/products/${row.productId}`}>
                                                {row.seoSlug}
                                            </Link>
                                        ) : (
                                            row.seoSlug || '—'
                                        )}
                                    </td>
                                    <td className="px-3 py-2">{row.nombre || '—'}</td>
                                    <td className="px-3 py-2">
                                        <span className="inline-flex flex-wrap items-center gap-2">
                                            <StatusBadge status={row.status} size="sm" />
                                            {row.reason ? <span className="text-xs text-muted-foreground">{row.reason}</span> : null}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
